import { useState } from 'react';
import { Button } from './Button';
import { ErrorDetailsDialog } from './dialogs/ErrorDetailsDialog';

export interface ErrorDisplayProps {
  error: Error | { message: string };
  entityName?: string; // e.g., "machines", "alarms"
  title?: string; // heading above the message, e.g. "The alert could not be saved"
}

/**
 * Parses an EDMS error message to extract the status code and JSON details
 * Returns null if not an EDMS error
 */
function parseEdmsError(errorMessage: string): {
  statusCode: number;
  summary: string;
  jsonDetails: string | null;
} | null {
  // Match patterns like "EDMS API Server Error (500): {...}" or "EDMS API Error (404): {...}"
  const edmsErrorRegex = /EDMS API (?:Server )?Error \((\d+)\):\s*([\s\S]*)/;
  const match = errorMessage.match(edmsErrorRegex);

  if (!match) {
    return null;
  }

  const statusCode = parseInt(match[1], 10);
  const details = match[2]?.trim();

  // Check if details is JSON
  let jsonDetails: string | null = null;
  if (details) {
    try {
      // Try to parse to verify it's valid JSON
      JSON.parse(details);
      jsonDetails = details;
    } catch {
      // Not valid JSON, treat as plain text
      jsonDetails = details;
    }
  }

  return {
    statusCode,
    summary: `EDMS API Server Error (${statusCode})`,
    jsonDetails,
  };
}

/**
 * ErrorDisplay - Shows error messages with special handling for EDMS API errors
 *
 * For EDMS 500 errors:
 * - Shows a clean summary message
 * - Provides a "Show error details" button to view full JSON in a dialog
 *
 * For other errors:
 * - Shows the error message directly
 */
export function ErrorDisplay({
  error,
  entityName = 'data',
  title = 'Error loading data',
}: ErrorDisplayProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  const errorMessage = error.message || 'An unknown error occurred';
  const parsedEdmsError = parseEdmsError(errorMessage);

  // Check if this is an EDMS 500+ error (server-side issue)
  const isEdmsServerError =
    parsedEdmsError && parsedEdmsError.statusCode >= 500;

  if (isEdmsServerError && parsedEdmsError.jsonDetails) {
    // Show clean message with details button for EDMS server errors
    return (
      <div
        className='bg-red-050 mx-auto max-w-100 rounded-lg border border-red-300
          px-6 py-4 text-center'
      >
        <p className='mb-2 text-lg font-semibold text-red-800'>{title}</p>
        <p className='mb-3 text-sm text-red-700'>
          Failed to fetch {entityName}: {parsedEdmsError.summary}
        </p>
        <Button
          variant='tertiary'
          onClick={() => setDetailsOpen(true)}
          className='text-red-700 hover:text-red-800'
        >
          Show error details
        </Button>

        <ErrorDetailsDialog
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
          title='EDMS API Error Details'
          errorDetails={parsedEdmsError.jsonDetails}
        />
      </div>
    );
  }

  // Default error display for non-EDMS or client errors
  return (
    <div
      className='bg-red-050 mx-auto max-w-75 rounded-lg border border-red-300
        px-6 py-4 text-center'
    >
      <p className='mb-2 text-lg font-semibold text-red-800'>{title}</p>
      <p className='text-sm wrap-break-word whitespace-normal text-red-700'>
        {errorMessage}
      </p>
    </div>
  );
}
